import React from 'react';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema, softwareAppSchema, faqSchema } from '../utils/structuredData';
import { ArrowRightIcon, CheckCircle2Icon, SearchIcon, TrendingUpIcon, ZapIcon, SparklesIcon, ExternalLinkIcon, StarIcon, ImageIcon, LinkIcon, CodeIcon, GaugeIcon, MapIcon, ShieldCheckIcon, RocketIcon, GlobeIcon } from 'lucide-react';
const SHOPIFY_URL = 'https://apps.shopify.com/seo-suite';
function InstallButton({
  size = 'md'
}: {
  size?: 'md' | 'lg';
}) {
  const pad = size === 'lg' ? 'px-10 py-5 text-lg' : 'px-8 py-4 text-base';
  return <a href={SHOPIFY_URL} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center justify-center gap-2 bg-emerald-500 text-white ${pad} rounded-full font-bold hover:bg-emerald-400 transition-all duration-300 hover:scale-105 shadow-[0_0_30px_rgba(16,185,129,0.3)]`} data-id="element-1239" data-mp="w67e2vz5ue3">
      Install on Shopify <ExternalLinkIcon className="w-5 h-5" data-id="element-1240" data-mp="1xs3yu13uk8e9" />
    </a>;
}
function StarRow() {
  return <div className="flex items-center gap-1" data-id="element-1241" data-mp="y9y1i12wnzty">
      {Array.from({
      length: 5
    }).map((_, i) => <StarIcon key={i} className="w-4 h-4 text-emerald-400 fill-emerald-400" data-id="element-1242" data-mp="138srbmzaionk" data-mpi={i} />)}
    </div>;
}
// Reusable "browser/app" frame for feature screenshots
function AppFrame({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="glass-card border-white/10 p-2 rounded-2xl relative shadow-2xl shadow-black/50" data-id="element-1243" data-mp="1h20kvk6xlu7w">
      <div className="bg-[#0A0A0A] rounded-xl overflow-hidden border border-white/5" data-id="element-1244" data-mp="1m02548g3fzky">
        <div className="h-10 border-b border-white/10 flex items-center px-4 gap-2 bg-white/5" data-id="element-1245" data-mp="1r2199kjnr0iy">
          <div className="w-3 h-3 rounded-full bg-red-500/50" data-id="element-1246" data-mp="1vyhwmcj19auz" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/50" data-id="element-1247" data-mp="3sscop03v4jdr" />
          <div className="w-3 h-3 rounded-full bg-green-500/50" data-id="element-1248" data-mp="3su5y2n5v1hpg" />
        </div>
        <div className="p-6" data-id="element-1249" data-mp="1mbjh86kwk01">{children}</div>
      </div>
    </div>;
}
interface FeatureBlock {
  eyebrow: string;
  title: string;
  desc: string;
  bullets: string[];
  mockup: React.ReactNode;
}
const FEATURE_BLOCKS: FeatureBlock[] = [{
  eyebrow: 'AI Meta Optimization',
  title: 'Rank higher with AI-written meta tags',
  desc: 'SEO Suite analyzes every product, collection, and blog post and writes keyword-rich meta titles and descriptions that Google loves and shoppers click, in bulk, in seconds.',
  bullets: ['AI-generated titles & descriptions for your entire catalog', 'Bulk-apply across thousands of pages in one click', 'Live Google preview so you see results before publishing'],
  mockup: <div className="space-y-4" data-id="element-1250" data-mp="haokggg3s33e">
        <div className="flex items-center justify-between" data-id="element-1251" data-mp="hbpuqipaolk2">
          <span className="text-sm font-semibold text-white" data-id="element-1252" data-mp="vafsm0jxup9h">
            Meta Tag Generator
          </span>
          <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-full border border-emerald-400/20" data-id="element-1253" data-mp="r94a0ezhf4as">
            AI Ready
          </span>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/5 p-4" data-id="element-1254" data-mp="19vbs35y31ggu">
          <p className="text-blue-400 text-sm font-medium mb-1 truncate" data-id="element-1255" data-mp="1evga50en2j96">
            Organic Cotton T-Shirt — Soft &amp; Sustainable | Your Store
          </p>
          <p className="text-emerald-400/70 text-xs mb-2" data-id="element-1256" data-mp="15urw45p8wiwa">
            yourstore.com › products › organic-cotton-tee
          </p>
          <p className="text-gray-400 text-xs leading-relaxed" data-id="element-1257" data-mp="r53ut4h2dk8k">
            Shop our breathable organic cotton t-shirts. Ethically made,
            ultra-soft, and built to last. Free shipping on orders over $50.
          </p>
        </div>
        <div className="flex items-center justify-between text-xs" data-id="element-1258" data-mp="m7k61amqpp2d">
          <span className="text-gray-500" data-id="element-1259" data-mp="m7tugts4zo5y">SEO score</span>
          <span className="text-emerald-400 font-bold" data-id="element-1260" data-mp="87j4dsynidlu">Excellent · 96</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden" data-id="element-1261" data-mp="d7f61ljvlsy8">
          <div className="h-full w-[96%] bg-emerald-500 rounded-full" data-id="element-1262" data-mp="1fkadztr6t7el" />
        </div>
      </div>
}, {
  eyebrow: 'Image SEO',
  title: 'Faster pages, better image rankings',
  desc: 'Heavy images kill your speed score and your rankings. SEO Suite compresses every image without quality loss and auto-writes descriptive ALT text so you show up in Google Images too.',
  bullets: ['Lossless bulk image compression for the whole store', 'Automatic descriptive ALT tags for accessibility & SEO', 'Lazy-loading and next-gen formats for faster load times'],
  mockup: <div className="space-y-3" data-id="element-1263" data-mp="nsuhn7ztw6b6">
        <div className="flex items-center justify-between mb-1" data-id="element-1264" data-mp="nu5k94hjf80p">
          <span className="text-sm font-semibold text-white" data-id="element-1265" data-mp="iuasyztuogf4">
            Image Optimizer
          </span>
          <span className="text-xs text-white font-bold" data-id="element-1266" data-mp="2i7opl0ht7uwk">12,450 optimized</span>
        </div>
        {[{
      name: 'hero-banner.jpg',
      before: '2.4 MB',
      after: '310 KB'
    }, {
      name: 'product-01.png',
      before: '1.8 MB',
      after: '240 KB'
    }, {
      name: 'lifestyle-shot.jpg',
      before: '3.1 MB',
      after: '410 KB'
    }].map(img => <div key={img.name} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 p-3" data-id="element-1267" data-mp="2zg7oq2xot06k">
            <div className="w-9 h-9 rounded-md bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0" data-id="element-1268" data-mp="21uju9nkskrxo">
              <ImageIcon className="w-4 h-4 text-emerald-400" data-id="element-1269" data-mp="2br502b4my72k" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1270" data-mp="2bt9evo42oqhm">
              <p className="text-xs text-gray-300 truncate" data-id="element-1271" data-mp="2ftjjawc7v8ud">{img.name}</p>
              <p className="text-[11px] text-gray-500" data-id="element-1272" data-mp="3n6bt4cip93ym">
                <span className="line-through" data-id="element-1273" data-mp="3n5sccz4sclm6">{img.before}</span> →{' '}
                <span className="text-emerald-400" data-id="element-1274" data-mp="3r6m1twxvm8h6">{img.after}</span>
              </p>
            </div>
            <CheckCircle2Icon className="w-4 h-4 text-emerald-400 shrink-0" data-id="element-1275" data-mp="1s1kjvrzmoacl" />
          </div>)}
      </div>
}, {
  eyebrow: 'Rich Results',
  title: 'Win rich snippets with structured data',
  desc: 'SEO Suite injects valid JSON-LD structured data so Google can show your star ratings, prices, and stock status right in the search results, driving dramatically higher click-through rates.',
  bullets: ['Automatic Product, Breadcrumb & Organization schema', 'Star ratings & pricing eligible for rich snippets', 'Validated against Google’s structured-data rules'],
  mockup: <div className="space-y-4" data-id="element-1276" data-mp="1i6smao61pnht">
        <span className="text-sm font-semibold text-white" data-id="element-1277" data-mp="1de98bq3g4qch">
          Google Rich Result Preview
        </span>
        <div className="rounded-lg border border-white/10 bg-white/5 p-4" data-id="element-1278" data-mp="pnwuxzpfm1ei">
          <p className="text-blue-400 text-sm font-medium mb-1" data-id="element-1279" data-mp="kpbq1mz8itjq">
            Premium Leather Wallet | Your Store
          </p>
          <div className="flex items-center gap-2 mb-2" data-id="element-1280" data-mp="2wcf7wwjvhkah">
            <StarRow data-id="element-1281" data-mp="2wauexbd7i9eq" />
            <span className="text-xs text-gray-400" data-id="element-1282" data-mp="3135gsjric59x">4.9 (612) · In stock</span>
          </div>
          <p className="text-emerald-400 text-sm font-bold mb-1" data-id="element-1283" data-mp="2nc8z3nz4d8hn">$89.00</p>
          <p className="text-gray-400 text-xs leading-relaxed" data-id="element-1284" data-mp="2n9wbau6ok3wo">
            Handcrafted full-grain leather wallet with RFID protection.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-emerald-400" data-id="element-1285" data-mp="3g1zh483vguj1">
          <CodeIcon className="w-4 h-4" data-id="element-1286" data-mp="3k2rmurdjchak" /> JSON-LD valid — 0 errors
        </div>
      </div>
}, {
  eyebrow: 'Technical SEO',
  title: 'Never lose traffic to broken links again',
  desc: 'Deleted a product? Changed a URL? SEO Suite scans your store 24/7, catches every 404, and lets you fix it with a one-click 301 redirect, so you keep your rankings and your customers.',
  bullets: ['Automatic 404 detection across your whole store', 'One-click 301 redirects to recover lost link equity', 'Bulk redirect manager with import/export'],
  mockup: <div className="space-y-3" data-id="element-1287" data-mp="2mad80fpr7cje">
        <div className="flex items-center justify-between" data-id="element-1288" data-mp="2ma3ckclxwd6b">
          <span className="text-sm font-semibold text-white" data-id="element-1289" data-mp="2hba0momawu89">
            Broken Link Manager
          </span>
          <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-full border border-emerald-400/20" data-id="element-1290" data-mp="2cdiao057vi6p">
            Auto-scanning
          </span>
        </div>
        {[{
      from: '/products/old-hoodie',
      to: '/products/new-hoodie'
    }, {
      from: '/collections/summer-22',
      to: '/collections/summer'
    }].map(r => <div key={r.from} className="rounded-lg border border-white/10 bg-white/5 p-3" data-id="element-1291" data-mp="2vb1kh5zi14u6">
            <div className="flex items-center gap-2 text-xs" data-id="element-1292" data-mp="3sstav0yatxgb">
              <LinkIcon className="w-3.5 h-3.5 text-emerald-400 shrink-0" data-id="element-1293" data-mp="3nzgnsv99pypo" />
              <span className="text-gray-400 truncate" data-id="element-1294" data-mp="4a15a4tujmp">{r.from}</span>
              <ArrowRightIcon className="w-3 h-3 text-gray-600 shrink-0" data-id="element-1295" data-mp="453oo2ecxwf2" />
              <span className="text-emerald-400 truncate" data-id="element-1296" data-mp="45dd3ljr7vin">{r.to}</span>
            </div>
          </div>)}
        <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-1297" data-mp="96jahwod06yi">
          <CheckCircle2Icon className="w-4 h-4 text-emerald-400" data-id="element-1298" data-mp="e4uis2z0a9em" /> 38 redirects
          active · 0 unresolved 404s
        </div>
      </div>
}, {
  eyebrow: 'AI Content & Blogs',
  title: 'AI-written content that ranks — on Google and AI search',
  desc: 'SEO Suite writes SEO-optimized product descriptions and full blog posts for you, then prepares your store for AI search engines, not just Google. It even generates an llms.txt file so AI crawlers can understand your store.',
  bullets: ['AI blog posts & product descriptions in your brand voice', 'Content tuned for both Google and AI search engines', 'Auto-generated llms.txt so AI crawlers understand your store'],
  mockup: <div className="space-y-4" data-id="element-1299" data-mp="2dsc82216ruj2">
        <div className="flex items-center justify-between" data-id="element-1300" data-mp="2huz3se0z9zlj">
          <span className="text-sm font-semibold text-white" data-id="element-1301" data-mp="2hte4yc7mruhv">
            AI Blog Generator
          </span>
          <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-full border border-emerald-400/20 flex items-center gap-1" data-id="element-1302" data-mp="2rljxpo5pify4">
            <SparklesIcon className="w-3 h-3" data-id="element-1303" data-mp="2wjv7jyw04qgc" /> Generating
          </span>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-2" data-id="element-1304" data-mp="31iook1ahyzl9">
          <p className="text-sm font-medium text-white" data-id="element-1305" data-mp="35l33wbeyjh7s">
            5 Ways Organic Cotton Beats Synthetic Fabric
          </p>
          <div className="space-y-1.5" data-id="element-1306" data-mp="3ajw8fec9nxvt">
            <div className="h-2 rounded-full bg-white/10 w-full" data-id="element-1307" data-mp="1b7bo0u61c13w" />
            <div className="h-2 rounded-full bg-white/10 w-11/12" data-id="element-1308" data-mp="1b94vucsdwrdh" />
            <div className="h-2 rounded-full bg-white/10 w-4/5" data-id="element-1309" data-mp="1g7g4ik6y0vu4" />
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2.5" data-id="element-1310" data-mp="18n5gydm4juj4">
          <GlobeIcon className="w-4 h-4" data-id="element-1311" data-mp="13uuk5jmokous" /> Optimized for Google &amp; AI search
          · llms.txt updated
        </div>
      </div>
}];
const BENEFITS = [{
  icon: <MapIcon className="w-6 h-6 text-emerald-400" data-id="element-1312" data-mp="tzap0zfxxu73" />,
  title: 'Smart Sitemaps',
  desc: 'Always-fresh XML sitemaps submitted automatically for faster indexing.'
}, {
  icon: <GaugeIcon className="w-6 h-6 text-emerald-400" data-id="element-1313" data-mp="1mmdn5qgpg2wt" />,
  title: 'Speed Insights',
  desc: 'Core Web Vitals monitoring with actionable fixes to boost your score.'
}, {
  icon: <GlobeIcon className="w-6 h-6 text-emerald-400" data-id="element-1314" data-mp="1dm8n61duc6so" />,
  title: 'Multi-Language SEO',
  desc: 'Hreflang tags handled automatically for international storefronts.'
}, {
  icon: <ShieldCheckIcon className="w-6 h-6 text-emerald-400" data-id="element-1315" data-mp="26ti0abha8mh" />,
  title: 'No-Code Setup',
  desc: 'Installs in minutes. No developer, no theme edits, no headaches.'
}];
const STATS = [{
  value: '+145%',
  label: 'Avg. organic traffic lift'
}, {
  value: '12,450+',
  label: 'Images optimized per store'
}, {
  value: '98/100',
  label: 'Average SEO health score'
}, {
  value: '5.0★',
  label: 'Merchant rating'
}];
const TESTIMONIALS = [{
  quote: 'Our organic traffic nearly doubled in two months. The AI meta tags alone were worth it. We stopped writing them by hand entirely.',
  name: 'Sarah L.',
  role: 'Founder, Apparel Brand'
}, {
  quote: 'Rich snippets got us star ratings in Google and our click-through rate jumped. Setup took ten minutes with zero code.',
  name: 'Daniel R.',
  role: 'Ecommerce Manager'
}, {
  quote: 'The broken-link manager saved rankings we would have lost after a big catalog cleanup. Genuinely essential.',
  name: 'Priya M.',
  role: 'Shopify Plus Merchant'
}];
const FAQS = [{
  q: 'Do I need any coding or SEO experience?',
  a: 'None at all. SEO Suite is fully no-code. It installs in minutes, runs automatically in the background, and gives you plain-English recommendations.'
}, {
  q: 'Will it slow down my store?',
  a: 'The opposite. SEO Suite compresses images and improves Core Web Vitals, which typically makes your store faster, not slower.'
}, {
  q: 'Is there a free plan?',
  a: 'Yes. You can start completely free, optimize your store, and upgrade only when you need advanced automation and unlimited pages.'
}, {
  q: 'How quickly will I see results?',
  a: 'Technical fixes apply instantly. Ranking and traffic improvements typically show within a few weeks as Google re-crawls your optimized pages.'
}];
export function SEOSuitePage() {
  usePageSEO({
    canonical: '/seo-suite',
    keywords: 'Shopify SEO app, SEO Suite, AI SEO Shopify, AI blog generator Shopify, AI search engine optimization, auto meta tags Shopify, JSON-LD Shopify, image optimizer Shopify, llms.txt Shopify',
    ogType: 'product',
    jsonLd: [breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Apps',
      path: '/apps'
    }, {
      name: 'SEO Suite',
      path: '/seo-suite'
    }]), {
      ...softwareAppSchema({
        name: 'SEO Suite',
        description: 'AI-powered Shopify SEO app that audits your store, fixes SEO issues automatically, writes AI blog & product content, and optimizes for both Google and AI search engines.',
        path: '/seo-suite',
        category: 'BusinessApplication'
      }),
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5.0',
        reviewCount: '3'
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      }
    }, faqSchema(FAQS.map(f => ({
      question: f.q,
      answer: f.a
    })))],
    title: 'SEO Suite: AI SEO & Blogs for Shopify | Design Flow Digitals',
    description: 'SEO Suite is an AI-powered Shopify SEO app that audits your store and fixes issues automatically — meta tags, alt text, broken links, and schema. It writes AI blog & product content and optimizes for both Google and AI search engines. Free plan available.'
  });
  return <div className="w-full" data-id="element-1316" data-mp="2c9u0wngwglt5">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center pt-32 pb-20 overflow-hidden" data-id="element-1317" data-mp="27ckfdiwmsual">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 via-black to-black pointer-events-none" data-id="element-1318" data-mp="22d7kcavlwgwx" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-1319" data-mp="1xfxvoxg1k9a5" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1320" data-mp="k9veq4ya8h9">
          <div data-id="element-1321" data-mp="1q3hv6qvt4kic">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-emerald-500/30 bg-emerald-500/10" data-id="element-1322" data-mp="1q2znmy3p2jv4">
              <SparklesIcon className="w-4 h-4 text-emerald-400" data-id="element-1323" data-mp="1uvst5o6zlw89" />
              <span className="text-sm font-medium text-emerald-300" data-id="element-1324" data-mp="1g63fithpjzr3">
                Latest Release · Shopify App
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" data-id="element-1325" data-mp="1l2bx0djdo51f">
              Smarter SEO, higher rankings
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 mb-8 leading-relaxed" data-id="element-1326" data-mp="2dtluim1ypy3j">
              SEO Suite is the AI-powered Shopify app that audits your store,
              fixes SEO issues automatically, writes AI blog & product content,
              and optimizes for both Google and AI search engines — on
              autopilot.
            </p>
            <div className="flex items-center gap-4 mb-10" data-id="element-1327" data-mp="og8o8raahzzs">
              <StarRow data-id="element-1328" data-mp="sgsjtqf7svjt" />
              <span className="text-sm text-gray-400" data-id="element-1329" data-mp="sh2cxmvk3itq">
                5.0 rating · Free plan available
              </span>
            </div>
            <div className="flex flex-wrap gap-4" data-id="element-1330" data-mp="fxdql36oken8">
              <InstallButton size="lg" data-id="element-1331" data-mp="fwlsnlpm24ts" />
              <a href="#features" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-1332" data-mp="uuec2oya3dd9">
                See how it works
              </a>
            </div>
          </div>

          {/* Hero Mockup */}
          <div className="relative" data-id="element-1333" data-mp="3tdcsiggougdx">
            <div className="absolute inset-0 bg-emerald-500/20 blur-[100px] rounded-full" data-id="element-1334" data-mp="3t9xelgyr3de3" />
            <div className="relative z-10" data-id="element-1335" data-mp="bxmssfiqky97">
              <AppFrame data-id="element-1336" data-mp="74a8uip05fms">
                <div className="flex justify-between items-center mb-6" data-id="element-1337" data-mp="741t8j1hvw7w">
                  <h3 className="text-lg font-bold text-white" data-id="element-1338" data-mp="3fjtv2xr99xsc">
                    SEO Health Score
                  </h3>
                  <span className="text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full text-sm font-bold border border-emerald-400/20" data-id="element-1339" data-mp="3akr24bojvi0q">
                    98/100
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-1340" data-mp="2u1kams8yd1c7">
                  <div className="bg-white/5 p-4 rounded-lg border border-white/5" data-id="element-1341" data-mp="2z059oocgxez2">
                    <p className="text-xs text-gray-400 mb-1" data-id="element-1342" data-mp="332i3qfhurog0">
                      Organic Traffic
                    </p>
                    <p className="text-xl font-bold text-white flex items-center gap-1.5" data-id="element-1343" data-mp="3801wekfas3rh">
                      +145%{' '}
                      <TrendingUpIcon className="w-4 h-4 text-emerald-400" data-id="element-1344" data-mp="3cybqsst770c1" />
                    </p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-lg border border-white/5" data-id="element-1345" data-mp="3ht8xvu6x2zma">
                    <p className="text-xs text-gray-400 mb-1" data-id="element-1346" data-mp="3ms29ti6k2ikc">
                      Images Optimized
                    </p>
                    <p className="text-xl font-bold text-white" data-id="element-1347" data-mp="3vqeakd14ywqi">12,450</p>
                  </div>
                </div>
                <div className="space-y-2" data-id="element-1348" data-mp="1k3u19tk9di14">
                  {['Meta Tags Optimized', 'Broken Links Fixed', 'Schema Added'].map(item => <div key={item} className="flex items-center justify-between p-2.5 bg-emerald-500/10 rounded-lg border border-emerald-500/20" data-id="element-1349" data-mp="1b3fd8mfwtotx">
                      <div className="flex items-center gap-2.5" data-id="element-1350" data-mp="22ta971y9wcrk">
                        <CheckCircle2Icon className="w-4 h-4 text-emerald-400" data-id="element-1351" data-mp="1yryjyxjb9tjw" />
                        <span className="text-sm text-emerald-100" data-id="element-1352" data-mp="1u18c9dnexem1">{item}</span>
                      </div>
                      <span className="text-xs text-emerald-400" data-id="element-1353" data-mp="1p2x6be08no93">
                        Automated
                      </span>
                    </div>)}
                </div>
              </AppFrame>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="border-y border-white/5 bg-[#050505]" data-id="element-1354" data-mp="2zekww9iiswr2">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-1355" data-mp="2uf80oy5redbt">
          {STATS.map(s => <div key={s.label} className="text-center" data-id="element-1356" data-mp="2pfby93cpmtyd">
              <p className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-1357" data-mp="2phydlp5uedr5">
                {s.value}
              </p>
              <p className="text-sm text-gray-500" data-id="element-1358" data-mp="3i4j4ypmbipup">{s.label}</p>
            </div>)}
        </div>
      </section>

      {/* Outcomes */}
      <section className="section-padding bg-black" data-id="element-1359" data-mp="3d67wojbo8nel">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1360" data-mp="38epr1hypdyna">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1361" data-mp="38cwmc87nhohx">
            Built to grow your store, not just your checklist
          </h2>
          <p className="text-xl text-gray-400" data-id="element-1362" data-mp="4pmi8n09t243">
            Every feature ladders up to the only three things that matter for
            your business.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1363" data-mp="3w2qy0mg8h70l">
          {[{
          icon: <TrendingUpIcon className="w-7 h-7 text-emerald-400" data-id="element-1364" data-mp="c95is75k5e0f" />,
          title: 'Rank higher',
          desc: 'Clean technical SEO and optimized content push your products up the search results where buyers actually look.'
        }, {
          icon: <SearchIcon className="w-7 h-7 text-emerald-400" data-id="element-1365" data-mp="m5ie19gr3zxa" />,
          title: 'Get found',
          desc: 'Rich snippets, image SEO, and fast indexing put your store in front of high-intent shoppers across Google.'
        }, {
          icon: <RocketIcon className="w-7 h-7 text-emerald-400" data-id="element-1366" data-mp="100awafo0zu7k" />,
          title: 'Sell more',
          desc: 'More qualified organic traffic means more sales, without paying for every single click.'
        }].map(o => <div key={o.title} className="glass-card p-8 border-white/10 hover:border-emerald-500/30 transition-colors" data-id="element-1367" data-mp="1ewa4rrf1rwd8">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-6 border border-emerald-500/20" data-id="element-1368" data-mp="p8mw0kh76clm">
                {o.icon}
              </div>
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-1369" data-mp="yypuh0738irc">{o.title}</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1370" data-mp="u6y1kj0gm0y1">{o.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Detailed Feature Blocks with mockups */}
      <section id="features" className="bg-[#020202] border-y border-white/5" data-id="element-1371" data-mp="12zrvnmkkf4vy">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 space-y-28" data-id="element-1372" data-mp="131vwtwr2xpsj">
          {FEATURE_BLOCKS.map((f, i) => <div key={f.title} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-1373" data-mp="1cxz1padbg2j6" data-mpi={i}>
              <div className={i % 2 === 1 ? 'lg:order-2' : ''} data-id="element-1374" data-mp="3jooywzoxc9p7">
                <p className="text-sm font-bold text-emerald-400 uppercase tracking-widest mb-4" data-id="element-1375" data-mp="3epu93dsjhkvl">
                  {f.eyebrow}
                </p>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight" data-id="element-1376" data-mp="1u94l9d16nhst">
                  {f.title}
                </h3>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1377" data-mp="1khqgoqudjrst">
                  {f.desc}
                </p>
                <ul className="space-y-4" data-id="element-1378" data-mp="1pf9uxqr2o4kr">
                  {f.bullets.map(b => <li key={b} className="flex items-start gap-3 text-gray-300" data-id="element-1379" data-mp="1bgacx1y2enxk">
                      <CheckCircle2Icon className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" data-id="element-1380" data-mp="1fg12517y0vzz" />
                      <span data-id="element-1381" data-mp="11kgux31ku28e">{b}</span>
                    </li>)}
                </ul>
              </div>
              <div className={i % 2 === 1 ? 'lg:order-1' : ''} data-id="element-1382" data-mp="2vwdop2nhrk6m">
                {f.mockup && <AppFrame data-id="element-1383" data-mp="2vuappt4d2gpr">{f.mockup}</AppFrame>}
              </div>
            </div>)}
        </div>
      </section>

      {/* Benefits grid */}
      <section className="section-padding bg-black" data-id="element-1384" data-mp="10th2c1z94srb">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1385" data-mp="10quv63g9wf9m">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1386" data-mp="vvfi3dq3ane9">Everything else, handled</h2>
          <p className="text-xl text-gray-400" data-id="element-1387" data-mp="rrr9qstmvnry">
            SEO Suite covers the full technical stack so nothing slips through
            the cracks.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-1388" data-mp="hu34h8yeixvj">
          {BENEFITS.map(b => <div key={b.title} className="glass-card p-6 border-white/10 hover:border-emerald-500/30 transition-colors" data-id="element-1389" data-mp="d2lax5ypjzkl">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-5 border border-emerald-500/20" data-id="element-1390" data-mp="43812c6o98zc">
                {b.icon}
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1391" data-mp="3vb3r7if890h5">{b.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed" data-id="element-1392" data-mp="3qay5eivn286z">{b.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-[#050505] border-y border-white/5" data-id="element-1393" data-mp="2aq5o5wpqtx3s">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1394" data-mp="2fps3cbp4tnf8">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1395" data-mp="2fo79ypek0aiy">
            Merchants love the results
          </h2>
          <p className="text-xl text-gray-400" data-id="element-1396" data-mp="2jpkcapiftvl4">
            Rated 5.0 by the Shopify stores growing their organic and AI-search
            traffic with SEO Suite.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1397" data-mp="1rymp20yub371">
          {TESTIMONIALS.map(t => <div key={t.name} className="glass-card p-8 border-white/10" data-id="element-1398" data-mp="20t12dxzt83ni">
              <StarRow data-id="element-1399" data-mp="20v5ev4bryaze" />
              <p className="text-gray-300 leading-relaxed my-6" data-id="element-1400" data-mp="14behfcdxvi1u">“{t.quote}”</p>
              <div data-id="element-1401" data-mp="149btqz09e10a">
                <p className="font-bold text-white" data-id="element-1402" data-mp="18chwvxs2rc2j">{t.name}</p>
                <p className="text-sm text-gray-500" data-id="element-1403" data-mp="18awz7zagrd0g">{t.role}</p>
              </div>
            </div>)}
        </div>
      </section>

      {/* Pricing */}
      <section className="section-padding bg-black" data-id="element-1404" data-mp="26nj6438r3wld">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1405" data-mp="26mq3negtrt91">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1406" data-mp="2lcxg9dk5ii1t">
            Start free. Upgrade when you grow.
          </h2>
          <p className="text-xl text-gray-400" data-id="element-1407" data-mp="2gf5medzy4luz">
            No risk, no code, no contracts. Install and start optimizing today.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch" data-id="element-1408" data-mp="1nqhysbrq5ke9">
          {/* Free */}
          <div className="glass-card p-8 border-white/10 flex flex-col" data-id="element-1409" data-mp="21q983z7aqclx">
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1410" data-mp="1xncs8g954ulo">Free</h3>
            <p className="text-gray-400 mb-6" data-id="element-1411" data-mp="3d4mqioja5zuk">Perfect to get started</p>
            <div className="mb-8" data-id="element-1412" data-mp="3d2tjv98o3fmk">
              <span className="text-5xl font-bold text-white" data-id="element-1413" data-mp="393b9v8idwoyi">$0</span>
              <span className="text-gray-400" data-id="element-1414" data-mp="39547jyb0ryud">/mo</span>
            </div>
            <ul className="space-y-3.5 mb-8 flex-1" data-id="element-1415" data-mp="3glrbbxg73s3u">
              {['Basic SEO audit once per day', '5 AI content generations per day', '5 image compressions per day', 'View and inspect meta tags', 'Product schema markup'].map(f => <li key={f} className="flex items-start gap-3 text-gray-300 text-sm" data-id="element-1416" data-mp="3bpakuwlix1nl">
                  <CheckCircle2Icon className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" data-id="element-1417" data-mp="870izirsy4z0" />{' '}
                  <span data-id="element-1418" data-mp="3po0jignzr9e2">{f}</span>
                </li>)}
            </ul>
            <a href={SHOPIFY_URL} target="_blank" rel="noopener noreferrer" className="w-full text-center py-4 rounded-full font-bold text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-1419" data-mp="3ultnr45qhtbp">
              Install Free
            </a>
          </div>

          {/* Pro */}
          <div className="glass-card p-8 border-emerald-500/30 bg-emerald-500/5 relative overflow-hidden flex flex-col" data-id="element-1420" data-mp="2n1ob2ylgrn7w">
            <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-1421" data-mp="2i7twg9kptyfo">
              MOST POPULAR
            </div>
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1422" data-mp="29ajj8ioc7z84">Pro</h3>
            <p className="text-gray-400 mb-6" data-id="element-1423" data-mp="35ziorpxjemwm">
              For stores serious about growth
            </p>
            <div className="mb-1" data-id="element-1424" data-mp="31yp0uwk3h83q">
              <span className="text-5xl font-bold text-white" data-id="element-1425" data-mp="31vjcxkn7cc9l">$8.99</span>
              <span className="text-gray-400" data-id="element-1426" data-mp="2wwzqlj4p4ygp">/mo</span>
            </div>
            <p className="text-sm text-emerald-400 mb-8" data-id="element-1427" data-mp="2s09a4uozc0ur">
              or $89.99/year — save 17%
            </p>
            <ul className="space-y-3.5 mb-8 flex-1" data-id="element-1428" data-mp="1lkfhyhymgbka">
              {['Unlimited SEO audits', '100 AI content generations per month', '50 image compressions per month', '5 AI blog posts per month', 'Edit and bulk update meta tags', 'All schema markup types', 'Broken link monitoring', 'Automated SEO scans'].map(f => <li key={f} className="flex items-start gap-3 text-gray-300 text-sm" data-id="element-1429" data-mp="1l50i3erotbug">
                  <CheckCircle2Icon className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" data-id="element-1430" data-mp="1p62ja155zxz2" />{' '}
                  <span data-id="element-1431" data-mp="1u4nflphapxi8">{f}</span>
                </li>)}
            </ul>
            <a href={SHOPIFY_URL} target="_blank" rel="noopener noreferrer" className="w-full text-center py-4 rounded-full font-bold bg-emerald-500 text-white hover:bg-emerald-400 transition-colors shadow-[0_0_20px_rgba(16,185,129,0.3)]" data-id="element-1432" data-mp="1z633dpauyi4b">
              Start 7-day free trial
            </a>
          </div>

          {/* Premium */}
          <div className="glass-card p-8 border-white/10 flex flex-col" data-id="element-1433" data-mp="2g4mrjk932jg2">
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1434" data-mp="2kwxukvz4elcu">Premium</h3>
            <p className="text-gray-400 mb-6" data-id="element-1435" data-mp="2ky8uultcxaz7">Everything, unlimited</p>
            <div className="mb-1" data-id="element-1436" data-mp="276udskj0fjus">
              <span className="text-5xl font-bold text-white" data-id="element-1437" data-mp="2c442tz2hlti3">$14.99</span>
              <span className="text-gray-400" data-id="element-1438" data-mp="2c1zzpjcewyiv">/mo</span>
            </div>
            <p className="text-sm text-emerald-400 mb-8" data-id="element-1439" data-mp="2zuzwl1qi6yrz">
              or $144.99/year — save 19%
            </p>
            <ul className="space-y-3.5 mb-8 flex-1" data-id="element-1440" data-mp="33uh4s2xbea0u">
              {['Unlimited AI content generations', 'Unlimited image compressions', 'Unlimited AI blog posts', 'AI auto-generated meta tags', 'Custom AI prompt templates', 'Daily automated SEO scans', 'Auto-redirect broken links', 'Priority support'].map(f => <li key={f} className="flex items-start gap-3 text-gray-300 text-sm" data-id="element-1441" data-mp="1buuuenm5vby3">
                  <CheckCircle2Icon className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" data-id="element-1442" data-mp="16yxe2hawe5lb" />{' '}
                  <span data-id="element-1443" data-mp="1kvsxn3orzffm">{f}</span>
                </li>)}
            </ul>
            <a href={SHOPIFY_URL} target="_blank" rel="noopener noreferrer" className="w-full text-center py-4 rounded-full font-bold text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-1444" data-mp="1fy0wdj28qgyr">
              Start 7-day free trial
            </a>
          </div>
        </div>
        <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto" data-id="element-1445" data-mp="x6hw3jzlph0f">
          All charges are billed in USD. Recurring and usage-based charges are
          billed every 30 days.
        </p>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-[#020202] border-t border-white/5" data-id="element-1446" data-mp="4ht3k9cm2k0a">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1447" data-mp="24jkaagegv81t">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1448" data-mp="29joqe5bgq0ri">
            Frequently asked questions
          </h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-5" data-id="element-1449" data-mp="2e9vfe69vznaa">
          {FAQS.map(f => <div key={f.q} className="glass-card p-6 border-white/10" data-id="element-1450" data-mp="2j98cdjuh2aql">
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1451" data-mp="2jbko3f9mzov7">{f.q}</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1452" data-mp="1lnamv2hod555">{f.a}</p>
            </div>)}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 px-6 text-center bg-gradient-to-b from-transparent to-emerald-900/20" data-id="element-1453" data-mp="1vk5klj64j1id">
        <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1454" data-mp="1zl920a3tl5lp">
          Ready to dominate search results?
        </h2>
        <p className="text-xl text-gray-400 mb-4 max-w-2xl mx-auto" data-id="element-1455" data-mp="36zu6e77n5uhl">
          Install SEO Suite today and start turning Google into your most
          profitable sales channel.
        </p>
        <div className="flex items-center justify-center gap-3 mb-10" data-id="element-1456" data-mp="zdu73rwfrdvr">
          <StarRow data-id="element-1457" data-mp="vbpd2s22sf8l" />
          <span className="text-sm text-gray-400" data-id="element-1458" data-mp="vb78n859eupl">
            5.0 rating · Free plan · Installs in minutes
          </span>
        </div>
        <InstallButton size="lg" data-id="element-1459" data-mp="qjfq9kw9bvaj" />
      </section>
    </div>;
}